import { useEffect } from "react";
import { useOnlineStore } from "../../store/onlineStore";
import { useTasksStore } from "../../store/tasksStore";
import { WifiOff, CloudUpload, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

/**
 * Floating chip that appears when the tab is offline OR when there are
 * pending items in the offline outbox. Doubles as a "Retry now" affordance.
 *
 * Also mounts the outbox-replay <-> tasksStore reconciliation bridge so
 * optimistic tasks get their real ids once the network catches up.
 */
export default function OfflineIndicator() {
  const { t } = useTranslation();
  const online = useOnlineStore((s) => s.online);
  const pending = useOnlineStore((s) => s.pendingCount);
  const syncing = useOnlineStore((s) => s.syncing);
  const drain = useOnlineStore((s) => s.drain);
  const reconcile = useTasksStore((s) => s.reconcileReplayedTask);
  const dropOptimistic = useTasksStore((s) => s.dropOptimisticTask);

  useEffect(() => {
    const onReplayed = (e) => {
      const { entry, response } = e.detail || {};
      if (!entry || !response) return;
      if (entry.url === "/tasks" && (entry.method || "POST") === "POST") {
        // Task creation completed — swap temp id.
        reconcile(response, {
          title: entry.body?.title,
          project_id: entry.body?.project_id,
          list_id: entry.body?.list_id,
        });
      }
    };
    const onDrained = () => {
      // Nothing extra — the store is up-to-date via onReplayed events. We
      // just surface a toast so the user knows things went through.
      toast.success(t("offline.synced_toast"));
    };
    const onDropped = (e) => {
      const { entry, error } = e.detail || {};
      if (entry?.url === "/tasks" && entry?.method === "POST") {
        dropOptimistic({
          title: entry.body?.title,
          project_id: entry.body?.project_id,
        });
      }
      toast.error(
        (typeof error === "string" ? error : t("offline.rejected")) +
          " · " +
          (entry?.body?.title || entry?.url),
      );
    };
    window.addEventListener("serpix:outbox-replayed", onReplayed);
    window.addEventListener("serpix:outbox-drained", onDrained);
    window.addEventListener("serpix:outbox-dropped", onDropped);
    return () => {
      window.removeEventListener("serpix:outbox-replayed", onReplayed);
      window.removeEventListener("serpix:outbox-drained", onDrained);
      window.removeEventListener("serpix:outbox-dropped", onDropped);
    };
  }, [reconcile, dropOptimistic, t]);

  const visible = !online || pending > 0;
  if (!visible) return null;

  return (
    <div
      data-testid="offline-indicator"
      className="fixed inset-x-3 bottom-24 z-40 mx-auto flex max-w-sm items-center gap-2 rounded-full border border-zinc-200 bg-white/95 px-3 py-2 shadow-xl shadow-zinc-900/10 backdrop-blur sm:inset-x-auto sm:left-6 sm:bottom-6"
    >
      {!online ? (
        <>
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-red-100 text-red-600">
            <WifiOff className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1 text-xs">
            <div className="truncate font-semibold text-zinc-900">
              {t("offline.title")}
            </div>
            <div className="truncate text-zinc-500">
              {pending > 0
                ? t("offline.pending_count", { count: pending })
                : t("offline.will_retry")}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-violet-100 text-violet-600">
            {syncing ? (
              <CloudUpload className="h-4 w-4 animate-pulse" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
          </div>
          <div className="min-w-0 flex-1 text-xs">
            <div className="truncate font-semibold text-zinc-900">
              {syncing ? t("offline.syncing") : t("offline.queued")}
            </div>
            <div className="truncate text-zinc-500">
              {t("offline.pending_count", { count: pending })}
            </div>
          </div>
          {!syncing && (
            <button
              onClick={drain}
              data-testid="offline-retry-btn"
              className="flex-none rounded-full bg-zinc-950 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-zinc-800"
            >
              {t("offline.retry")}
            </button>
          )}
        </>
      )}
    </div>
  );
}
